import React, { useCallback, useRef, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { SectionTitle } from './SectionTitle';
import { FileText, Paperclip, Pencil, Trash2, Upload } from 'lucide-react';
import { formatFileSize } from '../../../../Helpers/FileUtils';
import { formatDateForDisplay } from '../../../../Helpers/DateUtils';
import { WORK_ORDER_LABELS, workOrderSectionTestId } from '../../workOrderSections';
import ConfirmationModal from '../../../../Components/ConfirmationModal';
import { RenameAttachmentModal } from '../../../../Components/RenameAttachmentModal';
import './shared.css';

export interface AttachmentRow {
    key: string;
    /** What is shown: the custom display name when set, else the file name */
    name: string;
    /** The original file name, for the rename modal */
    fileName?: string;
    /** The custom display name when set, for the rename modal */
    displayName?: string;
    /** When given the name is a link opening in a new tab; otherwise `onOpen` is used */
    url?: string;
    sizeBytes?: number;
    /** Storage format (YYYY-MM-DD hh:mm:ss) */
    uploadedAt?: string;
    canRemove?: boolean;
    canRename?: boolean;
}

export interface AttachmentsListProps {
    attachments: AttachmentRow[];
    /** When given, a file input and drop zone are rendered */
    onUpload?: (files: File[]) => void;
    uploading?: boolean;
    /** Called once the user has confirmed the removal in the modal. */
    onRemove?: (key: string) => void;
    /** Called with the new display name (undefined = use the file name); throws to show an error in the modal. */
    onRename?: (key: string, displayName: string | undefined) => Promise<void>;
    onOpen?: (key: string) => void;
    error?: string;
}

export const AttachmentsList: React.FC<AttachmentsListProps> = ({
    attachments,
    onUpload,
    uploading = false,
    onRemove,
    onRename,
    onOpen,
    error
}) => {
    const [dragOver, setDragOver] = useState(false);
    const [removeKey, setRemoveKey] = useState<string | null>(null);
    const [renameKey, setRenameKey] = useState<string | null>(null);
    const [renaming, setRenaming] = useState(false);
    const removeTarget = removeKey ? attachments.find((a) => a.key === removeKey) : undefined;
    const renameTarget = renameKey ? attachments.find((a) => a.key === renameKey) : undefined;
    const inputRef = useRef<HTMLInputElement>(null);

    const handleRemoveConfirm = () => {
        if (removeKey && onRemove) onRemove(removeKey);
        setRemoveKey(null);
    };

    const handleRenameConfirm = async (displayName: string | undefined) => {
        if (!renameKey || !onRename) return;
        setRenaming(true);
        try {
            await onRename(renameKey, displayName);
            setRenameKey(null);
        } finally {
            setRenaming(false);
        }
    };

    const emitFiles = useCallback((list: FileList | null) => {
        if (!onUpload || !list || list.length === 0) return;
        onUpload(Array.from(list));
    }, [onUpload]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        emitFiles(e.target.files);
        e.target.value = '';
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        if (!dragOver) setDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        if (uploading) return;
        emitFiles(e.dataTransfer.files);
    };

    const renderName = (attachment: AttachmentRow) => {
        if (attachment.url) {
            return (
                <a
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wo-attachments__name"
                >
                    {attachment.name}
                </a>
            );
        }
        if (onOpen) {
            return (
                <Button
                    variant="link"
                    className="wo-attachments__name p-0 text-start"
                    onClick={() => onOpen(attachment.key)}
                >
                    {attachment.name}
                </Button>
            );
        }
        return <span className="wo-attachments__name">{attachment.name}</span>;
    };

    return (
        <div className="wo-attachments wo-card" data-testid={workOrderSectionTestId('attachments')}>
            <SectionTitle
                icon={Paperclip}
                title={WORK_ORDER_LABELS.sectionAttachments}
                count={attachments.length}
                onAdd={onUpload ? () => inputRef.current?.click() : undefined}
                addLabel={WORK_ORDER_LABELS.add}
                adding={uploading}
            />
            {onUpload && (
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    className="d-none"
                    aria-label="Välj filer"
                    onChange={handleInputChange}
                    disabled={uploading}
                />
            )}

            {error && (
                <Alert variant="danger" className="mb-2 py-2">{error}</Alert>
            )}

            {attachments.length === 0 ? (
                <p className="text-muted small mb-2">Inga bilagor</p>
            ) : (
                <ul className="list-unstyled mb-2">
                    {attachments.map((attachment) => {
                        const meta = [
                            attachment.sizeBytes !== undefined ? formatFileSize(attachment.sizeBytes) : null,
                            attachment.uploadedAt ? formatDateForDisplay(attachment.uploadedAt) : null
                        ].filter((part): part is string => Boolean(part));
                        return (
                            <li key={attachment.key} className="wo-attachments__row py-1">
                                <div className="d-flex align-items-center gap-2 wo-attachments__meta flex-grow-1">
                                    <FileText size={20} className="text-muted flex-shrink-0" />
                                    <div className="wo-attachments__meta">
                                        <div>{renderName(attachment)}</div>
                                        {meta.length > 0 && (
                                            <small className="text-muted">{meta.join(' · ')}</small>
                                        )}
                                    </div>
                                </div>
                                {attachment.canRename && onRename && (
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="flex-shrink-0"
                                        title="Byt namn"
                                        aria-label={`Byt namn på ${attachment.name}`}
                                        onClick={() => setRenameKey(attachment.key)}
                                    >
                                        <Pencil size={16} />
                                    </Button>
                                )}
                                {attachment.canRemove && onRemove && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="flex-shrink-0"
                                        aria-label={`${WORK_ORDER_LABELS.delete} ${attachment.name}`}
                                        onClick={() => setRemoveKey(attachment.key)}
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {onUpload && (
                <div
                    className={`wo-attachments__dropzone${dragOver ? ' wo-attachments__dropzone--active' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <Upload size={20} className="mb-1" />
                    <div className="small">
                        {dragOver ? 'Släpp filerna här' : 'Dra och släpp filer här, eller använd Lägg till'}
                    </div>
                </div>
            )}

            {onRename && (
                <RenameAttachmentModal
                    show={renameTarget !== undefined}
                    fileName={renameTarget?.fileName || renameTarget?.name || ''}
                    displayName={renameTarget?.displayName}
                    onHide={() => setRenameKey(null)}
                    onConfirm={handleRenameConfirm}
                    submitting={renaming}
                />
            )}

            {onRemove && (
                <ConfirmationModal
                    show={removeKey !== null}
                    title="Ta bort bilaga"
                    message={removeTarget ? `Vill du ta bort bilagan ${removeTarget.name}?` : 'Vill du ta bort bilagan?'}
                    confirmText="Ta bort bilaga"
                    onConfirm={handleRemoveConfirm}
                    onCancel={() => setRemoveKey(null)}
                />
            )}
        </div>
    );
};

export default AttachmentsList;
